import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import cx from 'classnames';
import {
  getExternalLinkPath,
  getLinkPath,
  useOffer,
  getBuyflowLink,
} from '../../utils/helpers';

import useTrailer from '../../hooks/useTrailer/useTrailer';
import { useNetwork } from '../../hooks/useNetwork/useNetwork';

import HeroCard from '../HeroCard';
import { ArrowLink, ButtonLink } from '../common';
import { Heading } from '../a11y';
import StrapiBannerDescription from '../StrapiBannerDescription';
import { buyflowUrlMap } from '../../utils/buyflowUrlMap';

import { IAssetTrailer, IMedia, INetwork } from '../../utils/models';
import { useVisitId } from '../../hooks/useVisitId';
import useContextToken from '../../hooks/useContextToken';

import './HeroBanner.scss';

interface HeroContextProps {
  hidden: boolean;
  isPlayingTrailer: boolean;
  isFullScreen: boolean;
  videoPlayer: any;
  trailerControls: any;
  isCarouselSlide?: boolean;
  layout: 'full' | 'split';
}
const HeroContext = createContext<HeroContextProps>({} as HeroContextProps);

export interface HeroBannerProps {
  layout?: 'full' | 'split';
  hidden?: boolean;
  trailer: IAssetTrailer | null;
  isCarouselSlide?: boolean;
  children?: ReactNode;
  assetType?: string;
  isCustomLandingPage?: boolean;
}

const HeroBanner = ({
  layout = 'full',
  hidden = false,
  trailer,
  isCarouselSlide = false,
  children,
  assetType,
  isCustomLandingPage = false,
}: HeroBannerProps) => {
  const { isPlayingTrailer, isFullScreen, videoPlayer, trailerControls } =
    useTrailer(trailer, hidden);

  return (
    <HeroContext.Provider
      value={{
        layout,
        hidden,
        isCarouselSlide,
        isPlayingTrailer,
        isFullScreen,
        videoPlayer,
        trailerControls,
      }}
    >
      <section
        className={cx({
          'hero-banner': true,
          [layout]: true,
          [`${layout}-event`]: assetType === 'event',
          full__landingpage: isCustomLandingPage,
        })}
      >
        {children}
      </section>
    </HeroContext.Provider>
  );
};

interface HeroImageProps {
  imageUrl?: string;
  heroImage?: any;
  imageAltText: string;
  isLiveEvent?: boolean;
  hasGradient?: boolean;
  isCustomLandingPage?: boolean;
}

const HeroImage = ({
  imageUrl = '',
  heroImage,
  imageAltText = '',
  isLiveEvent = false,
  hasGradient = true,
  isCustomLandingPage = false,
}: HeroImageProps) => {
  const { isPlayingTrailer, isFullScreen, videoPlayer } =
    useContext(HeroContext);

  return (
    <HeroCard
      hasGradient={hasGradient}
      imageUrl={imageUrl}
      heroImage={heroImage}
      isLiveEvent={isLiveEvent}
      imageAltText={imageAltText}
      isPlayingTrailer={isPlayingTrailer}
      isFullScreen={isFullScreen}
      videoPlayer={videoPlayer}
      isCustomLandingPage={isCustomLandingPage}
    />
  );
};

interface MediaContentProps {
  media: IMedia;
  network?: INetwork;
  analytics: any;
}

const MediaContent = ({ media, network, analytics }: MediaContentProps) => {
  const { layout, hidden, isCarouselSlide, trailerControls } =
    useContext(HeroContext);
  const { title, description, shortDescription, offer } = media;

  const currentOffer = useOffer(offer);

  const isRental = currentOffer.price?.value;

  const {
    networkImagePath,
    networkDisplayName,
    shouldShowNetworkImage,
    addNetworkButton,
  } = useNetwork(currentOffer, network);

  const visitId = useVisitId();
  const { contextToken } = useContextToken();

  let [unauthLink, authLink] = ['', ''];
  if (addNetworkButton.buttonLink in buyflowUrlMap) {
    [unauthLink, authLink] = buyflowUrlMap[addNetworkButton.buttonLink];
  }

  const buyflowLink = getBuyflowLink(unauthLink, authLink);

  return (
    <div
      className={cx({
        'hero-banner__content': true,
        'carousel-slide': isCarouselSlide,
      })}
    >
      <div className="hero-banner__content-inner">
        <header className="header">
          {shouldShowNetworkImage ? (
            <img
              className="header__logo"
              src={`${networkImagePath}&sourceType=colorhybrid&width=120`}
              alt={`${networkDisplayName} logo`}
              loading="lazy"
              width="48"
              height="48"
            />
          ) : null}

          {/* Title */}
          {isCarouselSlide ? (
            <h2 className="header__title">{title}</h2>
          ) : (
            <Heading className="header__title">{title}</Heading>
          )}
        </header>
        <div className="hero-banner__body">
          {/* Trailer Controls */}
          {trailerControls}
          {/* Description */}
          <div>
            <p className="description">{shortDescription || description}</p>
            {/* Actions */}
            <div className="hero-banner__actions">
              <div className="hero-banner__buttons">
                {/* Watch/Rent */}
                <ButtonLink
                  className="link"
                  type="secondary"
                  path={getExternalLinkPath(media, visitId, contextToken)}
                  ariaHidden={hidden}
                  tabIndex={hidden ? -1 : 0}
                  onClick={() => {
                    analytics.track('SpectrumOnDemand_selectAction_watchNow', {
                      currPageElemUiName:
                        'SpectrumOnDemand_selectAction_watchNow',
                    });
                  }}
                  ariaLabel={`${isRental ? 'Rent' : 'Watch'} ${title}`}
                >
                  {isRental ? 'Rent' : 'Watch'}
                </ButtonLink>

                {/* Add Network */}
                {!isCarouselSlide && addNetworkButton.buttonLink && (
                  <div>
                    <ArrowLink
                      text={addNetworkButton.buttonText}
                      path={
                        buyflowLink
                          ? `${buyflowLink}&visitId=${visitId}${contextToken}`
                          : `${addNetworkButton.buttonLink}&visitId=${visitId}${contextToken}`
                      }
                      external={true}
                      rel="noreferrer"
                      target="_blank"
                      ariaLabel={`More Info for ${title}`}
                      tealiumConfig={{
                        dataLinkType: 'hero__arrow-link',
                        dataLinkName: `More Info - ${title}`,
                      }}
                    />
                  </div>
                )}

                {/* More Info */}
                {isCarouselSlide && (
                  <div>
                    <ArrowLink
                      text="More Info"
                      path={getLinkPath(media)}
                      ariaHidden={hidden}
                      ariaLabel={`More Info for ${title}`}
                      tabIndex={hidden ? -1 : 0}
                      tealiumConfig={{
                        dataLinkType: 'hero__arrow-link',
                        dataLinkName: `More Info - ${title}`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {layout === 'split' && <div className="gradient" role="presentation" />}
    </div>
  );
};

const StrapiContent = ({
  item,
  analytics,
  assetType,
  isCustomLandingPage = false,
}: {
  item: any;
  analytics: any;
  assetType?: string;
  isCustomLandingPage?: boolean;
}) => {
  const { hidden, isCarouselSlide, trailerControls } = useContext(HeroContext);
  const { title, description, shortDescription, CTA, availability } = item;
  const { buttonText, buttonURL } = CTA || {};
  const { date, time } = availability || {};

  const arrowLinkPath = useMemo(() => {
    const { __typename, slug } = item;
    if (__typename === 'allStrapi_ComponentCarouselTitleSlide') {
      return item.type === 'Movie' ? `/movies/${slug}/` : `/tv/${slug}/`;
    }
    if (__typename === 'allStrapi_ComponentCarouselFeaturedEvent') {
      return `/events/${slug}/`;
    }
    return '';
  }, [item]);

  return (
    <div
      className={cx({
        'hero-banner__content': true,
        'carousel-slide': isCarouselSlide,
        'hero-banner__content-event': assetType === 'event',
      })}
    >
      <div className="hero-banner__content-inner">
        <header className="header">
          {/* Title */}
          {isCarouselSlide ? (
            <h2 className="header__title">{title}</h2>
          ) : (
            <Heading className="header__title">{title}</Heading>
          )}
          {date && (
            <p className="header__availability">
              Available {date} {time && `, `} {time}
            </p>
          )}
        </header>
        <div className="hero-banner__body">
          {/* Trailer */}
          {trailerControls}

          <div>
            {/* Description */}
            <StrapiBannerDescription
              description={description}
              shortDescription={shortDescription}
              hidden={hidden}
              isCustomLandingPage={isCustomLandingPage}
            />
            <div className="hero-banner__actions">
              <div className="hero-banner__buttons">
                {buttonText && buttonURL && (
                  <ButtonLink
                    type="secondary"
                    path={buttonURL}
                    ariaHidden={hidden}
                    tabIndex={hidden ? -1 : 0}
                    onClick={() =>
                      analytics.track('selectAction', {
                        currPageElemStdName: `Order - ${title}`,
                        msgCategory: 'navigation',
                        msgTriggeredBy: 'user',
                        opType: 'navigationClick',
                      })
                    }
                    tealiumConfig={{
                      dataLinkType: 'hero__outline-button',
                      dataLinkName: `${buttonText} - ${title}`,
                    }}
                  >
                    {buttonText}
                  </ButtonLink>
                )}

                {isCarouselSlide && arrowLinkPath ? (
                  <div>
                    <ArrowLink
                      text="More Info"
                      path={arrowLinkPath}
                      ariaLabel={`More Info for ${item.title}`}
                      ariaHidden={hidden}
                      tabIndex={hidden ? -1 : 0}
                      tealiumConfig={{
                        dataLinkType: 'arrow-link',
                        dataLinkName: `More Info for ${item.title}`,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

HeroBanner.HeroImage = HeroImage;
HeroBanner.MediaContent = MediaContent;
HeroBanner.StrapiContent = StrapiContent;

export default HeroBanner;
